import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCMS } from '../cms';
import { buildNavLinks } from '../cms/navigation-helpers';
import { hasWebappSession } from '../lib/lunar-user';
import { track } from '../lib/posthog';
import { paygPricingEnabled } from '../lib/feature-flags';

const SCROLL_SECTIONS: Record<string, string> = {};

/** Map nav labels to their route paths for active-state matching. */
const LABEL_TO_ROUTE: Record<string, string> = {
  'Use Cases': '/use-cases',
  Enterprise: '/enterprise',
  Blog: '/blog',
  Pricing: '/pricing',
};

/** Map dropdown child labels to internal routes. */
const CHILD_ROUTES: Record<string, string> = {
  'LunarCrush + Claude': '/products/lunarcrush-claude',
  'LunarCrush API': '/products/lunarcrush-api',
  'LunarCrush MCP': '/products/lunarcrush-mcpserver',
  'LunarCrush Discover': '/products/lunarcrush-discover',
  'LunarCrush CLI': '/products/lunarcrush-cli',
  'LunarCrush Collections': '/products/lunarcrush-custom-collections',
  'Custom Collections': '/products/lunarcrush-custom-collections',
  'Fantasy Football': '/products/fantasy-football',
};

/** Map English CMS labels → i18n translation keys for top-level links. */
const LABEL_TO_NAV_KEY: Record<string, string> = {
  Platform: 'nav.platform',
  'Use Cases': 'nav.useCases',
  Enterprise: 'nav.enterprise',
  Pricing: 'nav.pricing',
  Blog: 'nav.blog',
};

/** Map English CMS child labels → i18n translation keys for dropdowns. */
const LABEL_TO_PRODUCT_KEY: Record<string, string> = {
  'LunarCrush + Claude': 'nav.products.claude',
  'LunarCrush API': 'nav.products.api',
  'LunarCrush MCP': 'nav.products.mcp',
  'LunarCrush Discover': 'nav.products.discover',
  'LunarCrush CLI': 'nav.products.cli',
  'LunarCrush Collections': 'nav.products.collections',
  'Custom Collections': 'nav.products.customCollections',
  'Fantasy Football': 'nav.products.fantasy',
};

export function useAppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { navigation } = useCMS();
  const nav = buildNavLinks(navigation);

  const translateLabel = (label: string): string => {
    const key = LABEL_TO_NAV_KEY[label];
    return key ? t(key) : label;
  };

  const translateChildLabel = (label: string): string => {
    const key = LABEL_TO_PRODUCT_KEY[label];
    return key ? t(key) : label;
  };

  // Override nav structure: Product → LunarCrush API + LunarCrush Discover; Use Cases → plain link
  const links = nav.links
    .map((link) => {
      const isActive = LABEL_TO_ROUTE[link.label]
        ? location.pathname === LABEL_TO_ROUTE[link.label]
        : false;
      const translated = translateLabel(link.label);

      if (link.label === 'Platform') {
        return {
          ...link,
          label: translated,
          isActive: isActive || location.pathname.startsWith('/products/'),
          children: link.children?.map((child) => {
            const route = CHILD_ROUTES[child.label];
            const childLabel = translateChildLabel(child.label);
            if (route) {
              return { ...child, label: childLabel, onPress: () => navigate(route) };
            }
            return { ...child, label: childLabel };
          }),
        };
      }

      if (link.label === 'Use Cases') {
        return {
          label: translated,
          isActive,
          onPress: () => navigate('/use-cases'),
        };
      }

      if (link.label === 'Enterprise') {
        return {
          label: translated,
          isActive,
          onPress: () => navigate('/enterprise'),
        };
      }

      if (link.label === 'Blog') {
        return {
          label: translated,
          isActive: isActive || location.pathname.startsWith('/blog'),
          onPress: () => navigate('/blog'),
        };
      }

      if (link.label === 'Pricing') {
        return {
          label: translated,
          isActive,
          onPress: () => navigate('/pricing'),
        };
      }

      const sectionId = SCROLL_SECTIONS[link.label];
      if (sectionId) {
        return {
          ...link,
          label: translated,
          onPress: () => {
            if (location.pathname === '/') {
              document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
            } else {
              navigate('/', { state: { scrollTo: sectionId } });
            }
          },
        };
      }

      return { ...link, label: translated };
    });

  // DEV-10817: gate the signed-in nav (APP button + avatar) on the webapp's
  // own session, NOT the broad isSignedIn(). The legacy `lunar-UserSettings`
  // blob persists forever for anyone who ever used the old Expo site and is
  // never cleared on the new app's logout — so isSignedIn() lit the logged-in
  // nav for users who are actually signed OUT. hasWebappSession() ignores the
  // legacy blob and matches what the webapp considers a live session.
  const signedIn = hasWebappSession();

  return {
    links,
    // DEV-12734: the logged-out header now carries a single GET STARTED action
    // (Jon collapsed the old SIGN IN + SIGN UP pair; the sign-in icon variant
    // was rejected). It enters the new app's /login route — the same
    // destination the old SIGN IN button used (DEV-10448). Auth is OAuth-only;
    // /login's own flow hands off into pricing/plan selection. Full nav (not
    // react-router navigate) so the request crosses the build boundary under
    // the unified domain.
    cta: {
      label: t('nav.getStarted'),
      onPress: () => { track('nav_get_started_click'); window.location.assign('/login'); },
    },
    ...(signedIn && {
      userMenu: {
        // Jon (Oct 26): with PAYG on, APP opens the Dashboard, the signed-in
        // home of the PAYG app. Off (production today) it stays on /home.
        onAppPress: () => window.location.assign(paygPricingEnabled() ? '/dashboard' : '/home'),
        onAvatarPress: () => window.location.assign('/account'),
      },
    }),
  };
}
